import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from 'components/Button';
import {
    fetchAuthors,
    fetchStory,
    markNotRaw,
    markRaw,
    markReraw,
    newsroomError,
    publishStory,
    saveStory,
    unpublishStory,
    uploadLeadImage,
} from 'api/newsroom';
import {
    handleHidePopup,
    handleShowPopup,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import {
    fromLocalInput,
    namesExpert,
    sourceCredit,
    withOutboundRel,
} from 'utils/newsroom';
import { faqQuestionInputs } from 'utils/faq-sponsor';
import {
    cleanSpecialisms,
    sponsorDraft,
    sponsorProblem,
    sponsorStarted,
    templateComplete,
} from 'utils/story-sponsor';
import StoryEditor from './StoryEditor';
import RawPanel from './RawPanel';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const FIELDS = [
    'headline',
    'bylineAuthorId',
    'body',
    'seoTitle',
    'metaDescription',
    'focusKeyphrase',
    'sponsorName',
    'sponsorLogoUrl',
    'sponsorLogoBg',
    'sponsorProfileId',
    'sponsorImageUrl',
    'sponsorUrl',
    'sponsorStartsAt',
    'sponsorEndsAt',
    'leadImageAlt',
    'leadImageCaption',
    'leadImageCredit',
];

const LEAD_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

const leadImageDraft = (story) => ({
    leadImageAlt: story?.leadImageAlt ?? '',
    leadImageCaption: story?.leadImageCaption ?? '',
    leadImageCredit: story?.leadImageCredit ?? '',
    clearLeadImage: false,
});

const editorError = (message) =>
    Object.assign(new Error(message), { editorMessage: message });

const SPONSOR_DATES = ['sponsorStartsAt', 'sponsorEndsAt'];

const toDraft = (story, alert) => ({
    headline: story?.headline ?? alert.title ?? '',
    // The byline goes too, so a backend without authors yet keeps the name.
    byline: story?.byline ?? '',
    bylineAuthorId: story?.bylineAuthorId ? String(story.bylineAuthorId) : '',
    body: story?.body ?? '',
    seoTitle: story?.seoTitle ?? '',
    metaDescription: story?.metaDescription ?? '',
    focusKeyphrase: story?.focusKeyphrase ?? '',
    ...sponsorDraft(story),
    ...leadImageDraft(story),
    // The offer itself is sent from the Evergreen Sponsor box, not saved with the story.
    faqHeading: story?.faqHeading ?? '',
    faqQuestions: faqQuestionInputs(story?.faqQuestions),
});

const specialismsKey = (draft) =>
    JSON.stringify(cleanSpecialisms(draft.sponsorSpecialisms));

const faqKey = (draft) =>
    JSON.stringify([
        draft.faqHeading.trim(),
        draft.faqQuestions.map((q) => q.trim()),
    ]);

// The editor keeps its own HTML; outbound rel is added to what is saved, since
// feeding it back into CKEditor would reset the content on every keystroke.
const toSaved = (draft) => ({ ...draft, body: withOutboundRel(draft.body) });

// The date inputs hold local time; the API takes ISO.
const toPayload = (draft) => {
    const saved = toSaved(draft);
    SPONSOR_DATES.forEach((key) => {
        saved[key] = fromLocalInput(saved[key]);
    });
    saved.bylineAuthorId = saved.bylineAuthorId
        ? Number(saved.bylineAuthorId)
        : null;
    // The API takes the whole slot on every save; a template banner replaces an old image.
    if (templateComplete(saved)) saved.sponsorImageUrl = '';
    if (!sponsorStarted(saved)) saved.sponsorLogoUrl = '';
    const specialisms = cleanSpecialisms(saved.sponsorSpecialisms);
    saved.sponsorSpecialisms = specialisms.length ? specialisms : null;
    // Navy is the default the API stores as null, so leaving it alone never reads as a change.
    saved.sponsorLogoBg = saved.sponsorLogoBg === 'white' ? 'white' : null;
    saved.sponsorProfileId = saved.sponsorProfileId
        ? Number(saved.sponsorProfileId)
        : null;
    return saved;
};

const RAW_FIELDS = ['rawAt', 'rawExclusiveUntil', 'rawReviewedAt', 'rerawAt'];
const rawPatch = (alert) =>
    RAW_FIELDS.reduce((patch, key) => ({ ...patch, [key]: alert[key] }), {});

// Admin only: the Storify, Raw and Publish buttons, and the panels they open.
const Newsroom = ({
    alert,
    researchOn,
    // A re-offer sale pays for the sponsor slot, so it stays open while answers stay off.
    storySponsorOff = researchOn,
    // The alert's research sponsorship row: the full sponsorship offer's price and state.
    researchRow,
    onResearchRowChange,
    responsesCount,
    onAlertChange,
    onStoryChange,
    onEditingChange,
}) => {
    const dispatch = useDispatch();
    // undefined while loading, null when the alert has no story yet.
    const [story, setStory] = useState(undefined);
    const [draft, setDraft] = useState(null);
    const [panel, setPanel] = useState(null); // 'story' | 'raw' | null
    const [busy, setBusy] = useState(null);
    const [notice, setNotice] = useState(null);
    // The body as CKEditor last had it saved or loaded. CKEditor rewrites embeds
    // and the server sanitizes them, so the server's HTML never matches it.
    const [savedBody, setSavedBody] = useState(null);
    const [authors, setAuthors] = useState([]);
    const editorLoaded = useRef(false);

    useEffect(() => {
        fetchAuthors()
            .then(setAuthors)
            .catch((err) =>
                setNotice({
                    type: 'error',
                    text: newsroomError(
                        err,
                        'Could not load the authors. Reload the page before changing the byline.'
                    ),
                })
            );
    }, []);

    useEffect(() => {
        let live = true;
        setStory(undefined);
        setDraft(null);
        setPanel(null);
        setSavedBody(null);
        editorLoaded.current = false;
        fetchStory(alert.id)
            .then((data) => live && setStory(data ?? null))
            // "No story yet" comes back as an empty 200, so an error is a real failure.
            // Treating it as no story would let Storify overwrite the one that exists.
            .catch(
                (err) =>
                    live &&
                    setNotice({
                        type: 'error',
                        text: newsroomError(
                            err,
                            'Could not load the story. Reload the page before editing it.'
                        ),
                    })
            );
        return () => {
            live = false;
        };
    }, [alert.id]);

    useEffect(() => {
        onEditingChange?.(panel === 'story');
    }, [panel]);

    const dirty = useMemo(() => {
        if (!draft) return false;
        const saved = toDraft(story, alert);
        if (savedBody !== null) saved.body = savedBody;
        const current = toSaved(draft);
        return (
            FIELDS.some(
                (f) => (current[f] || '').trim() !== (saved[f] || '').trim()
            ) ||
            specialismsKey(current) !== specialismsKey(saved) ||
            faqKey(current) !== faqKey(saved) ||
            current.clearLeadImage
        );
    }, [draft, story, alert, savedBody]);

    // Don't lose a story by closing the tab.
    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    // One action at a time: a Publish pressed while a Save is still on its way would save
    // again from the version that Save is replacing, and the API would refuse it.
    const inFlight = useRef(false);
    const run = async (key, action, fallback) => {
        if (inFlight.current) return null;
        inFlight.current = true;
        setBusy(key);
        setNotice(null);
        try {
            return await action();
        } catch (err) {
            setNotice({
                type: 'error',
                text: err?.editorMessage || newsroomError(err, fallback),
            });
            return null;
        } finally {
            inFlight.current = false;
            setBusy(null);
        }
    };

    const togglePanel = (name) => {
        if (name === 'story' && !draft) setDraft(toDraft(story, alert));
        setPanel(panel === name ? null : name);
    };

    const change = (field, value) => {
        // The first body the editor reports is the loaded story, not an edit.
        if (field === 'body' && !editorLoaded.current) {
            editorLoaded.current = true;
            setSavedBody(withOutboundRel(value));
        }
        setDraft((current) =>
            current[field] === value ? current : { ...current, [field]: value }
        );
    };

    const persist = async () => {
        if (
            story?.leadImageUrl &&
            !draft.clearLeadImage &&
            !draft.leadImageAlt.trim()
        ) {
            throw editorError(
                'Add alt text for the lead image, or remove the image.'
            );
        }
        const sponsorMessage = sponsorProblem(draft);
        if (sponsorMessage) {
            throw editorError(sponsorMessage);
        }
        const sent = toPayload(draft);
        // The version this draft was loaded from: the API refuses the save if someone has saved since.
        const saved = await saveStory(alert.id, {
            ...sent,
            version: story ? story.version : null,
        });
        setStory(saved);
        setSavedBody(sent.body);
        // The API drops blank questions, so the draft takes its saved order back.
        setDraft((current) => ({
            ...current,
            sponsorImageUrl: saved.sponsorImageUrl ?? '',
            sponsorLogoUrl: saved.sponsorLogoUrl ?? '',
            faqHeading: saved.faqHeading ?? '',
            faqQuestions: faqQuestionInputs(saved.faqQuestions),
            ...leadImageDraft(saved),
        }));
        if (saved.publishedAt) onStoryChange(saved);
        return saved;
    };

    const handleSave = () =>
        run(
            'save',
            async () => {
                const saved = await persist();
                setNotice({
                    type: 'ok',
                    text: saved.publishedAt
                        ? 'Saved. The live story is updated.'
                        : 'Draft saved. Press Publish when it is ready.',
                });
            },
            'Could not save the story.'
        );

    const handleLeadImage = (file) =>
        run(
            'leadImage',
            async () => {
                if (!draft.leadImageAlt.trim()) {
                    throw editorError('Write the alt text before uploading.');
                }
                if (file.size > LEAD_IMAGE_MAX_BYTES) {
                    throw editorError(
                        'The lead image must be 10 MB or smaller.'
                    );
                }
                // The image goes onto a saved story. Other unsaved edits stay unsaved, so
                // uploading never pushes them to a live story.
                if (!story) await persist();
                const saved = await uploadLeadImage(alert.id, file, draft);
                setStory(saved);
                setDraft((current) => ({
                    ...current,
                    ...leadImageDraft(saved),
                }));
                if (saved.publishedAt) onStoryChange(saved);
                setNotice({
                    type: 'ok',
                    text: saved.publishedAt
                        ? 'Lead image saved. The live story shows it.'
                        : 'Lead image saved to the draft.',
                });
            },
            'Could not upload the lead image.'
        );

    const publish = () =>
        run(
            'publish',
            async () => {
                if (draft && dirty) await persist();
                const published = await publishStory(alert.id);
                setStory(published);
                onStoryChange(published);
                onAlertChange({ newsIndex: true });
                setPanel(null);
                setNotice({
                    type: 'ok',
                    text: 'Published. The story is live above the raw alert and in the Google News sitemap.',
                });
            },
            'Could not publish the story.'
        );

    const seededBy = alert.rawAt ? alert.seededBy : null;

    const handlePublish = () => {
        const body = draft ? draft.body : story?.body;
        if (!seededBy || namesExpert(body, seededBy.name)) return publish();
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                title: `${seededBy.name} is not named in the story`,
                body: 'This Raw was seeded by them, so they must be quoted. Publish anyway?',
                confirmTitle: 'Publish anyway',
                cancelTitle: 'Back to the story',
                onCancel: () => dispatch(handleHidePopup()),
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    await publish();
                    dispatch(setPopupLoading(false));
                    dispatch(handleHidePopup());
                },
            })
        );
    };

    const handleUnpublish = () => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                title: 'Unpublish the story?',
                body: 'The raw alert stays live, and the story is kept as a draft.',
                confirmTitle: 'Unpublish',
                cancelTitle: 'Cancel',
                onCancel: () => dispatch(handleHidePopup()),
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    await run(
                        'publish',
                        async () => {
                            const story = await unpublishStory(alert.id);
                            setStory(story);
                            onStoryChange(null);
                            onAlertChange({ newsIndex: false });
                            setNotice({
                                type: 'ok',
                                text: 'Story unpublished. It is saved as a draft.',
                            });
                        },
                        'Could not unpublish the story.'
                    );
                    dispatch(setPopupLoading(false));
                    dispatch(handleHidePopup());
                },
            })
        );
    };

    const handleRaw = (exclusiveUntil) =>
        run(
            'raw',
            async () => {
                const updated = await markRaw(alert.id, exclusiveUntil);
                onAlertChange(rawPatch(updated));
                setPanel(null);
                setNotice({
                    type: 'ok',
                    text: exclusiveUntil
                        ? 'Branded a Newspage Raw. It joins the archive when the exclusive ends.'
                        : 'Branded a Newspage Raw and added to the Raws archive.',
                });
            },
            'Could not make this a Raw.'
        );

    const handleReraw = () =>
        run(
            'reraw',
            async () => {
                const updated = await markReraw(alert.id);
                onAlertChange(rawPatch(updated));
                setPanel(null);
                setNotice({
                    type: 'ok',
                    text: 'ReRawed. It is back at the top of the Raws archive.',
                });
            },
            'Could not ReRaw this alert.'
        );

    const handleNotRaw = () =>
        run(
            'notRaw',
            async () => {
                const updated = await markNotRaw(alert.id);
                onAlertChange(rawPatch(updated));
                setPanel(null);
                setNotice({
                    type: 'ok',
                    text: 'Marked Not a Raw. It is out of the archive and the review queue.',
                });
            },
            'Could not update this alert.'
        );

    const published = Boolean(story?.publishedAt);
    const loading = story === undefined;

    return (
        <div className={styles.newsroom}>
            <div className={styles.bar}>
                <Button
                    title={
                        panel === 'story'
                            ? 'Close story'
                            : story
                            ? 'Edit story'
                            : 'Storify'
                    }
                    theme={panel === 'story' ? 'secondary' : 'primary'}
                    disabled={loading}
                    onClick={() => togglePanel('story')}
                />
                <Button
                    title={alert.rawAt ? 'Raw ✓' : 'Raw'}
                    theme={alert.rawAt ? 'success' : 'primary'}
                    onClick={() => togglePanel('raw')}
                />
                {published ? (
                    <Button
                        title={'Unpublish story'}
                        theme={'danger'}
                        loading={busy === 'publish'}
                        onClick={handleUnpublish}
                    />
                ) : (
                    <Button
                        title={'Publish'}
                        theme={'success'}
                        loading={busy === 'publish'}
                        disabled={loading || (!story && !dirty)}
                        onClick={handlePublish}
                    />
                )}
                <span className={styles.barStatus}>
                    {loading
                        ? 'Loading story…'
                        : published
                        ? 'Story live'
                        : story
                        ? 'Story draft saved'
                        : 'No story yet'}
                </span>
            </div>

            {notice && (
                <p
                    className={
                        notice.type === 'error' ? styles.error : styles.ok
                    }
                    role={notice.type === 'error' ? 'alert' : 'status'}
                >
                    {notice.text}
                </p>
            )}

            {panel === 'raw' && (
                <RawPanel
                    alert={alert}
                    responsesCount={responsesCount}
                    busy={busy}
                    onRaw={handleRaw}
                    onReraw={handleReraw}
                    onNotRaw={handleNotRaw}
                    onClose={() => setPanel(null)}
                />
            )}

            {panel === 'story' && draft && seededBy && (
                <p className={styles.sourceReminder} role="note">
                    {`This Raw was seeded by ${sourceCredit(
                        seededBy
                    )}. They must be quoted in the story.`}
                </p>
            )}

            {panel === 'story' && draft && (
                <StoryEditor
                    alert={alert}
                    researchOn={researchOn}
                    storySponsorOff={storySponsorOff}
                    researchRow={researchRow}
                    onResearchRowChange={onResearchRowChange}
                    story={story}
                    draft={draft}
                    authors={authors}
                    slug={alert.slug}
                    published={published}
                    dirty={dirty}
                    saving={busy === 'save'}
                    uploadingLeadImage={busy === 'leadImage'}
                    onLeadImage={handleLeadImage}
                    error={notice?.type === 'error' ? notice.text : null}
                    onChange={change}
                    onSponsorChange={(saved) => {
                        setStory(saved);
                        if (saved.publishedAt) onStoryChange(saved);
                    }}
                    onSave={handleSave}
                    onClose={() => setPanel(null)}
                />
            )}
        </div>
    );
};

export default Newsroom;
